// Pure helpers for the Needs Attention panel.
import type { ScheduleReviewNeedsAttentionType } from '@load-balancing/graphql/queries/schedule-review';
import type { ScheduleTeamFteFilterType } from '@load-balancing/types';

// Session-dismissal identity for a card. Week-scoped cards are namespaced by the
// viewed week so dismissing one week's flag never hides a different week's
// same-named flag; period-scoped cards (identical on every week) dismiss
// globally. Falls back to the bare key when the viewed week isn't known.
export function needsAttentionDismissId(
  item: ScheduleReviewNeedsAttentionType,
  weekKey?: string
): string {
  return item.scope === 'WEEK' && weekKey ? `${weekKey}:${item.key}` : item.key;
}

// What the panel should render, derived from the (nullable) server list plus the
// session-dismissed ids:
//  - `hidden`  — `null`/`undefined` items: the analyzer errored, or the query hasn't
//                loaded. This is the only case we hide — an unavailable panel must not
//                masquerade as a healthy schedule.
//  - `empty`   — nothing left to show: the server returned no flags, OR every card has
//                been dismissed this session. Both show the positive affirmation.
//  - `list`    — cards remain after dismissals.
// `null` (errored) is kept distinct from "no visible cards" so a failure never affirms.
export type NeedsAttentionPanelState =
  | { kind: 'hidden' }
  | { kind: 'empty' }
  | { kind: 'list'; visible: ReadonlyArray<ScheduleReviewNeedsAttentionType> };

export function needsAttentionPanelState(
  items: ReadonlyArray<ScheduleReviewNeedsAttentionType> | null | undefined,
  dismissedIds: ReadonlyArray<string>,
  weekKey?: string
): NeedsAttentionPanelState {
  if (items === null || items === undefined) return { kind: 'hidden' };

  const visible = items.filter(
    (item) => !dismissedIds.includes(needsAttentionDismissId(item, weekKey))
  );
  // No visible cards — whether the server returned none or the user dismissed them all —
  // is the all-clear affirmation.
  if (visible.length === 0) return { kind: 'empty' };
  return { kind: 'list', visible };
}

// FTE filter a "View in Team" card pre-selects. Only over_fte/under_fte carry FTE meaning;
// every other VIEW_IN_TEAM card opens unfiltered. The default → 'all' is load-bearing: it
// resets a stale FTE filter when switching off an FTE card — keep it total.
export function teamFteFilterForItem(
  item: ScheduleReviewNeedsAttentionType
): ScheduleTeamFteFilterType {
  if (item.key === 'over_fte') return 'above';
  if (item.key === 'under_fte') return 'below';
  return 'all';
}
